import type { ConversationContext } from '@/services/contextService';
import type { CandidateBallotpediaBlock } from '@/types/ballotpedia';
import { CONTEXT_VERSION } from '@/services/contextVersion';
import type { NationalStateSnapshot } from '@/hooks/useNationalBallot';
import { STATE_NAMES } from '@/constants/stateNames';
import { stateCodeFromComponents } from '@/utils/addressState';
import { DEFAULT_LANGUAGE, resolveSupportedLanguage } from '@/constants/languages';
import { filterOnBallotNationalStates } from '@/utils/ballotStatus';
import { isSpecialElection } from '@/utils/contestName';

/**
 * Client-side builders for the chat ``ConversationContext`` payload.
 *
 * Mirrors what the backend's ``/api/context/init`` produces for signed-in
 * users, but works from the data already available locally:
 *
 * - :func:`buildGuestPersonalContext` — full ballot context for a guest
 *   with a localStorage-cached address and the ``ballot_user`` fetched
 *   via ``/ballot/by-address``. Same shape the agent's prompt builder
 *   expects, so the same downstream prompt branches activate.
 *
 * - :func:`buildNationalSummaryContext` — slim summary for the national-
 *   view (no specific address). Lists upcoming statewide elections by
 *   state + date + contest count and includes per-election ``voting_dates``
 *   so the agent can answer "when's the registration deadline in MI" /
 *   "early voting start in PA" without forcing the user to enter an
 *   address. Still drops per-contest/candidate detail (heavy) — those
 *   should prompt an address entry.
 *
 * Field names and nesting are deliberately aligned with the backend's
 * ``ConversationContextV1`` schema (see ``context_controller.py``); the
 * agent's prompt builder reads field-by-field so a mismatch silently
 * drops parts of the context.
 */

interface AddressComponent {
  types?: string[];
  // Google Places API (New) returns ``shortText`` / ``longText``. Older
  // legacy SDK paths return ``shortName`` / ``short_name``. The current
  // VoteMate proxy normalizes to the new shape, but we keep the legacy
  // keys as fallbacks so a future SDK swap doesn't silently break the
  // state/county/zip extraction (and to mirror the signed-in flow's
  // resolution order in :func:`parseAddressComponents`).
  shortText?: string;
  longText?: string;
  shortName?: string;
  longName?: string;
  short_name?: string;
  long_name?: string;
}

const shortValue = (c: AddressComponent | undefined): string | undefined =>
  c?.shortText || c?.shortName || c?.short_name;

const longValue = (c: AddressComponent | undefined): string | undefined =>
  c?.longText || c?.longName || c?.long_name;

const findComponent = (
  components: AddressComponent[] | undefined,
  type: string
): AddressComponent | undefined =>
  components?.find((c) => Array.isArray(c.types) && c.types.includes(type));

export interface GuestBallotUser {
  next_election_date?: string;
  polling_locations?: Array<{
    election_date?: string;
    election_name?: string;
    polling_location_url: string;
  }>;
  elections?: Array<{
    election_date: string;
    election_name?: string;
    election_type?: string;
    guidance_phase?: string;
    contests?: Array<{
      id?: string;
      name?: string;
      title?: string;
      level?: string;
      districtName?: string;
      ocdId?: string;
      seatsUpForElection?: number;
      partisanPrimary?: string | null;
      source?: string;
      contestType?: string | null;
      stage?: string | null;
      candidates?: Array<{
        id?: string;
        fullName?: string;
        partyAffiliation?: string[] | null;
        isIncumbent?: boolean | null;
        // Ballotpedia geo records spell the status field cand_status / candidate_status.
        status?: string | null;
        cand_status?: string | null;
        candidate_status?: string | null;
        ballotpediaUrl?: string | null;
        source?: string;
        bp?: Pick<CandidateBallotpediaBlock, 'candidate_status' | 'is_write_in'> | null;
      }>;
    }>;
    ballot_measures?: Array<Record<string, unknown>>;
    voting_dates?: Record<string, unknown>;
  }>;
  election_offices?: Array<{
    name?: string;
    level?: string;
    links?: Array<{ url: string; label?: string }>;
  }>;
  election_office_url?: string;
  // Current elected officials for the address (Ballotpedia enrichment on the
  // by-address lookup). Optional — present only when enrichment succeeded.
  current_officeholders?: Array<{
    name?: string;
    office_name?: string;
    office_level?: string;
    office_branch?: string;
    district_name?: string;
    party?: string;
    status?: string;
    date_assumed_office?: string;
    date_term_ends?: string;
    url?: string;
    contacts?: Array<{ type?: string; value?: string }>;
  }>;
}

export type { AddressComponent };

/**
 * Canonicalize before sending: the backend coerces codes it doesn't know to
 * ``en``, so a raw ``zh-tw`` silently answers in the wrong language.
 */
const canonicalLanguage = (raw: string): string =>
  resolveSupportedLanguage(raw) ?? DEFAULT_LANGUAGE;

/**
 * Build a personal-guest ``ConversationContext`` from the data we have
 * client-side: the cached address components and the ballot returned by
 * /ballot/by-address.
 *
 * ``preferredLanguage`` is the guest's active interface language. A guest has no
 * account for the backend to read a preference from, so ``user.preferred_language``
 * here is what makes the first reply come back in their language (FR-19).
 *
 * ``countyPreview`` marks a ballot looked up from a public building in the
 * voter's county rather than their address. Its ZIP and polling places belong
 * to that building's precinct, so they are left out rather than handed to the
 * agent as the voter's, and ``source_type`` is ``county_sample`` so the agent
 * treats district races and officeholders as the sample's.
 */
export const buildGuestPersonalContext = (
  ballotUser: GuestBallotUser,
  addressComponents: AddressComponent[] | undefined,
  preferredLanguage: string = DEFAULT_LANGUAGE,
  { countyPreview = false }: { countyPreview?: boolean } = {}
): ConversationContext => {
  const countyComp = findComponent(addressComponents, 'administrative_area_level_2');
  const zipComp = countyPreview ? undefined : findComponent(addressComponents, 'postal_code');
  const pollingLocations = countyPreview ? [] : (ballotUser.polling_locations ?? []);
  const sourceType = countyPreview ? 'county_sample' : 'guest_localstorage';

  const elections = (ballotUser.elections ?? []).map((e) => {
    const contests = (e.contests ?? []).map((c) => ({
      id: c.id ?? '',
      title: c.title || c.name || '',
      // DW's descriptive label ("… school board At-large"), as the panel shows it.
      name: c.name,
      level: c.level,
      district_name: c.districtName,
      ocd_id: c.ocdId,
      // Floor to 1 to match the backend default (``ContextContest.seats``).
      // DW could in principle return 0 for a vacancy, which would silently
      // change downstream "elect N" wording.
      seats: c.seatsUpForElection ?? 1,
      partisan_primary: c.partisanPrimary ?? undefined,
      source: c.source,
      // Sent only for a special election, which can share its name with the
      // office's regular election; chat marks it the way the ballot does.
      contest_type: isSpecialElection(c) ? 'special' : undefined,
      stage: c.stage ?? undefined,
      candidates: (c.candidates ?? []).map((cand) => ({
        fullName: cand.fullName ?? '',
        id: cand.id,
        party:
          Array.isArray(cand.partyAffiliation) && cand.partyAffiliation.length > 0
            ? cand.partyAffiliation[0]
            : null,
        isIncumbent: cand.isIncumbent ?? null,
        status: cand.status ?? cand.cand_status ?? cand.candidate_status ?? null,
        // Ballotpedia provenance, mirroring the backend ``ContextCandidate``.
        // ``ballotpediaUrl`` is the citable article link the ballot record
        // carries (the only copy for a candidate whose ``bp`` block was pruned
        // as a duplicate, and for a Ballotpedia-only candidate that has no
        // block); ``source`` is ``'ballotpedia'`` when Ballotpedia listed the
        // candidate and Democracy Works did not.
        ballotpediaUrl: cand.ballotpediaUrl ?? null,
        source: cand.source,
        // The two fields the Ballot Preview reads to tell a write-in from a
        // candidate on the ballot (`candidateStatus.ts`). Without them a guest's
        // chat had no write-in status at all, and called a nominee on the ballot
        // a write-in beside a Preview listing him as a regular candidate.
        bp:
          cand.bp && (cand.bp.candidate_status || cand.bp.is_write_in)
            ? {
                candidate_status: cand.bp.candidate_status,
                is_write_in: cand.bp.is_write_in,
              }
            : undefined,
      })),
    }));
    return {
      election_date: e.election_date,
      election_name: e.election_name ?? '',
      election_type: e.election_type ?? '',
      guidance_phase: e.guidance_phase,
      contest_count: contests.length,
      contests,
      ballot_measures: (e.ballot_measures ??
        []) as ConversationContext['election']['elections'][number]['ballot_measures'],
      voting_dates:
        e.voting_dates as ConversationContext['election']['elections'][number]['voting_dates'],
      // Strictly match the election_date — a polling location lacking
      // ``election_date`` is dropped, not duplicated across every
      // election. The prior permissive ``!p.election_date ||`` form
      // silently propagated a URL to all elections if DW ever returned
      // a polling-location entry without a date.
      polling_locations: pollingLocations.filter(
        (p) => p.election_date && p.election_date === e.election_date
      ),
    };
  });

  return {
    v: CONTEXT_VERSION,
    user: {
      id: 'guest',
      preferred_language: canonicalLanguage(preferredLanguage),
      state: stateCodeFromComponents(addressComponents),
      county: longValue(countyComp) || shortValue(countyComp),
      zip_code: shortValue(zipComp),
      source_type: sourceType,
    },
    authorities: {
      polling_location_url: pollingLocations[0]?.polling_location_url,
      election_office_url: ballotUser.election_office_url,
      source_type: sourceType,
    },
    election: {
      next_election_date: ballotUser.next_election_date,
      elections,
      election_offices: (ballotUser.election_offices ?? []).map((o) => ({
        name: o.name ?? '',
        level: o.level,
        links: (o.links ?? []).map((l) => ({ url: l.url, label: l.label })),
      })),
      // Current officeholders, so guest chat grounds "who represents me" from
      // context the same way the signed-in path does. Drop any record missing
      // a name to match the backend's ``name``-required schema.
      //
      // ``contacts`` carries each office's phone/email/website. It reaches the
      // prompt so a voter asking who to call about a city service gets a real
      // channel; dropping it here left the agent web-searching for a number the
      // ballot screen was already showing.
      officeholders: (ballotUser.current_officeholders ?? [])
        .filter((oh) => Boolean(oh.name))
        .map((oh) => ({
          name: oh.name as string,
          office_name: oh.office_name,
          office_level: oh.office_level,
          office_branch: oh.office_branch,
          district_name: oh.district_name,
          party: oh.party,
          status: oh.status,
          date_assumed_office: oh.date_assumed_office,
          date_term_ends: oh.date_term_ends,
          url: oh.url,
          contacts: (Array.isArray(oh.contacts) ? oh.contacts : [])
            .filter(
              (c): c is { type: string; value: string } =>
                typeof c?.type === 'string' &&
                Boolean(c.type.trim()) &&
                typeof c?.value === 'string' &&
                Boolean(c.value.trim())
            )
            .map((c) => ({ type: c.type.trim(), value: c.value.trim() })),
        })),
      source_type: sourceType,
    },
  };
};

/**
 * Build a slim ``ConversationContext`` summarizing the national snapshot
 * for guests browsing without an address (and for signed-in users in
 * forced-national view). Lists upcoming statewide elections with state +
 * date + type + contest count + per-election ``voting_dates`` — enough to
 * answer registration/early-voting/mail-in deadline questions for any
 * state without forcing the user to enter an address.
 *
 * Does NOT include full contest/candidate data — the agent should suggest
 * entering an address rather than try to recall specifics.
 */
export const buildNationalSummaryContext = (
  states: NationalStateSnapshot[],
  preferredLanguage: string = DEFAULT_LANGUAGE
): ConversationContext => {
  const filteredStates = filterOnBallotNationalStates(states);
  const flat: ConversationContext['election']['elections'] = [];
  filteredStates.forEach((s) => {
    const stateName = STATE_NAMES[s.state_code] ?? s.state_code;
    s.elections.forEach((e) => {
      const contestCount = (e.contests ?? []).length;
      const measureCount = (e.ballotMeasures ?? []).length;
      flat.push({
        election_date: e.date,
        election_name: `${stateName} — ${e.description ?? 'Election'}`,
        election_type: e.type ?? '',
        guidance_phase: e.guidancePhase,
        contest_count: contestCount + measureCount,
        // Empty arrays; the slim summary intentionally omits the per-contest
        // detail so the agent learns scope (which states / when) without
        // hallucinating specifics it didn't actually see.
        contests: [],
        ballot_measures: [],
        // Pass through ``voting_dates`` when present. The snapshot script
        // attaches the per-election dates already (registration deadlines,
        // early-voting windows, absentee return-by, etc.); without these
        // the agent can answer "when's the next election in <state>" but
        // not "when's the registration deadline" — which is the question
        // a national-view guest actually asks. Size impact is bounded:
        // ~50 states × ~5 elections × ~15 string fields = well under any
        // token budget.
        voting_dates:
          e.voting_dates as ConversationContext['election']['elections'][number]['voting_dates'],
      });
    });
  });
  flat.sort((a, b) => (a.election_date ?? '').localeCompare(b.election_date ?? ''));

  const earliestDate = flat[0]?.election_date;

  return {
    v: CONTEXT_VERSION,
    user: {
      id: 'guest',
      preferred_language: canonicalLanguage(preferredLanguage),
      source_type: 'national_overview',
    },
    authorities: {
      source_type: 'national_overview',
    },
    election: {
      next_election_date: earliestDate,
      elections: flat,
      source_type: 'national_overview',
    },
  };
};
